import React, { useLayoutEffect, useRef } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  FloatingArrow,
} from '@floating-ui/react';
import './TourCallout.css';

const DEFAULT_MAX_W = 280;

/**
 * Tooltip-style callout anchored to a DOM node or virtual element. No buttons — tours end via map UI.
 */
export default function AnchoredTourCallout({
  show,
  reference,
  placement,
  fallbackPlacements,
  layoutTick,
  maxWidth = DEFAULT_MAX_W,
  interactive = false,
  children,
}) {
  const arrowRef = useRef(null);
  const open = Boolean(show && reference);

  const { refs, floatingStyles, context, update } = useFloating({
    strategy: 'fixed',
    open,
    placement,
    middleware: [
      offset(12),
      flip({ fallbackPlacements, padding: 8 }),
      shift({ padding: 10 }),
      arrow({ element: arrowRef, padding: 4 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useLayoutEffect(() => {
    if (reference) {
      refs.setReference(reference);
    }
  }, [reference, refs]);

  useLayoutEffect(() => {
    if (open) {
      update();
    }
  }, [open, layoutTick, update]);

  if (!open) return null;

  return (
    <div
      ref={refs.setFloating}
      className={`tour-callout tour-callout--floating${interactive ? ' tour-callout--interactive' : ''}`}
      style={{
        ...floatingStyles,
        zIndex: 1205,
        maxWidth: `min(${maxWidth}px, calc(100vw - 24px))`,
      }}
    >
      <FloatingArrow
        ref={arrowRef}
        context={context}
        width={12}
        height={6}
        tipRadius={1}
        fill="var(--theme-bg-header)"
        stroke="#d9d3c7"
        strokeWidth={1}
      />
      {children}
    </div>
  );
}
